// Production orders: every run, from paperwork to what actually came out.
//
// Mounted from AppMain when activeModule === 'productionOrders'.
//
// WHY ORDERS EXIST AT ALL
// -----------------------
// The console calculated requirements into thin air. Close the tab and the
// figures were gone, and nothing tied the sheet in somebody's hand to
// anything the system knew about. An order is the thing that persists: it
// carries a batch number, a costed snapshot of the recipe, and a record of
// who approved what.
//
// THE THREE STATES ARE THE WHOLE SCREEN
// -------------------------------------
//   DRAFT      print the sheet, then approve when ready to issue
//   APPROVED   materials are out; record what came back
//   COMPLETED  yield and loss, nothing left to do
//
// Each row offers only what its state allows, because a button that errors
// when pressed is worse than one that is not there.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      d = j.detail?.message || j.detail?.error || j.detail || d;
    } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm, fontSize: 13.5,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
};

const num = (v, dp = 2) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });

const STATUS_TONE = {
  DRAFT: 'neutral', APPROVED: 'info', COMPLETED: 'success',
  CANCELLED: 'danger', PLANNED: 'neutral', IN_PROGRESS: 'warning',
};

async function download(url, fallbackName, notify) {
  try {
    notify?.('Building the file...', 'info');
    const res = await authedFetch(url);
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || `Server returned ${res.status}`);
    }
    const disp = res.headers.get('Content-Disposition') || '';
    const m = disp.match(/filename="?([^"]+)"?/);
    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = m ? m[1] : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
    notify?.(`Downloaded ${a.download}`, 'success');
  } catch (e) { notify?.(`Download failed: ${e.message}`, 'error'); }
}

// ---------------------------------------------------------------------------

function NewOrder({ products, warehouses, onDone, onCancel, notify }) {
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!productId || !warehouseId || !Number(quantity)) {
      notify?.('Pick a product, a quantity and a store', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await req('/api/bom/production-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          quantity: Number(quantity),
          warehouse_id: warehouseId,
          notes: notes || null,
        }),
      });
      notify?.(r.detail, 'success');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15, marginBottom: space(1.5) }}>
        New production order
      </div>
      <Banner tone="info" title="Nothing leaves the store yet">
        Creating an order issues a batch number and costs the recipe. Materials
        are only issued when you approve it, so you can print the sheet and
        check the store first.
      </Banner>
      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      }}>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Product</div>
          <select style={inputStyle} value={productId}
                  onChange={(e) => setProductId(e.target.value)}>
            <option value="">Select a product…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.sku ? ` — ${p.sku}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Quantity to produce</div>
          <input style={inputStyle} value={quantity} inputMode="decimal"
                 onChange={(e) => setQuantity(e.target.value)} />
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Store to draw from</div>
          <select style={inputStyle} value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select a store…</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Notes (optional)</div>
          <input style={inputStyle} value={notes}
                 onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={create} disabled={busy}>Create order</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function OutputForm({ order, onDone, onCancel, notify }) {
  const [good, setGood] = useState('');
  const [damaged, setDamaged] = useState('0');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const planned = Number(order.quantity_planned || 0);
  const g = Number(good || 0);
  const d = Number(damaged || 0);
  const made = g + d;
  // Shown live so the operator sees the consequence of the figures BEFORE
  // saving, while the numbers can still be checked against the sheet.
  const efficiency = planned > 0 ? (g / planned) * 100 : null;
  const loss = made > 0 ? (d / made) * 100 : null;

  const save = async () => {
    if (good === '' || Number.isNaN(g) || g < 0) {
      notify?.('Enter the good output', 'error');
      return;
    }
    if (d > 0 && reason.trim().length < 3) {
      notify?.('Damaged output needs a reason', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await req(`/api/bom/production-orders/${order.id}/output`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity_good: g,
          quantity_damaged: d,
          damage_reason: d > 0 ? reason.trim() : null,
        }),
      });
      notify?.(r.detail, 'success');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15 }}>
        Record output — {order.order_number}
      </div>
      <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: space(1.5) }}>
        {order.product_name} · planned {num(planned, 0)} · batch {order.batch_number || '—'}
      </div>

      <Banner tone="info" title="Good units go to stock, damaged units do not">
        Good output enters stock against batch <strong>{order.batch_number}</strong>,
        so it can be traced in a recall. Damaged output is written off and never
        becomes sellable.
      </Banner>

      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      }}>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Good output</div>
          <input style={inputStyle} value={good} inputMode="decimal"
                 onChange={(e) => setGood(e.target.value)} />
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Damaged output</div>
          <input style={inputStyle} value={damaged} inputMode="decimal"
                 onChange={(e) => setDamaged(e.target.value)} />
        </label>
        <label style={{ gridColumn: 'span 2', minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Reason for damage {d > 0 && <span style={{ color: color.danger }}>(required)</span>}
          </div>
          <input style={inputStyle} value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. tubes split during sealing" />
        </label>
      </div>

      <div style={{
        marginTop: space(2), display: 'flex', gap: space(3), flexWrap: 'wrap',
        background: color.bg, padding: space(1.5), borderRadius: radius.md,
      }}>
        <div>
          <div style={{ fontSize: 12, color: color.textSecondary }}>Yield efficiency</div>
          <div style={{ fontSize: 20, fontWeight: 650, color: color.text }}>
            {efficiency === null ? '—' : `${efficiency.toFixed(1)}%`}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>good ÷ planned</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: color.textSecondary }}>Loss</div>
          <div style={{
            fontSize: 20, fontWeight: 650,
            color: loss > 0 ? color.danger : color.text,
          }}>
            {loss === null ? '—' : `${loss.toFixed(1)}%`}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>damaged ÷ made</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: color.textSecondary }}>Shortfall</div>
          <div style={{ fontSize: 20, fontWeight: 650, color: color.text }}>
            {num(planned - made)}
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted }}>planned − made</div>
        </div>
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={save} disabled={busy}>Record output</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export default function ProductionOrders({ notify }) {
  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [creating, setCreating] = useState(false);
  const [recording, setRecording] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const q = filter === 'ALL' ? '' : `?status=${filter}`;
      setOrders(await req(`/api/bom/production-orders${q}`));
    } catch (e) { setErr(e.message); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [p, w] = await Promise.all([
          req('/api/bom/products-with-bom').catch(() => []),
          req('/api/warehouses/?size=200').catch(() => ({ items: [] })),
        ]);
        // products-with-bom answers with product_id/product_name, and
        // /api/warehouses is paginated. Normalised here so the dropdowns do
        // not quietly render a list of blank options.
        const plist = Array.isArray(p) ? p : (p.items || []);
        setProducts(plist.map((x) => ({
          id: x.product_id || x.id,
          name: x.product_name || x.name,
          sku: x.product_sku || x.sku,
        })).filter((x) => x.id));
        const wlist = Array.isArray(w) ? w : (w.items || []);
        setWarehouses(wlist.map((x) => ({ id: x.id, name: x.name }))
                           .filter((x) => x.id));
      } catch { /* the form simply shows no options */ }
    })();
  }, []);

  const approve = async (order) => {
    let reason = null;
    setBusy(order.id);
    try {
      try {
        const r = await req(`/api/bom/production-orders/${order.id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: null }),
        });
        notify?.(r.detail, r.approved_with_shortage ? 'warning' : 'success');
        await load();
        return;
      } catch (e) {
        // A shortage is not a refusal: it is a question. Ask it, then retry
        // with the answer rather than making the user start again.
        if (!/reason is required/i.test(e.message)) throw e;
        reason = window.prompt(
          `${e.message}\n\nWhy is this being approved short? (at least 5 characters)`,
          '');
        if (reason === null) { notify?.('Approval cancelled', 'info'); return; }
        if (reason.trim().length < 5) {
          notify?.('A reason of at least 5 characters is required', 'error');
          return;
        }
      }
      const r = await req(`/api/bom/production-orders/${order.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notify?.(r.detail, 'warning');
      await load();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  if (err) return <ErrorBox msg={err} />;
  if (!orders) return <SkeletonCards n={4} />;

  if (creating) {
    return (
      <NewOrder
        products={products} warehouses={warehouses} notify={notify}
        onCancel={() => setCreating(false)}
        onDone={() => { setCreating(false); load(); }}
      />
    );
  }

  if (recording) {
    return (
      <OutputForm
        order={recording} notify={notify}
        onCancel={() => setRecording(null)}
        onDone={() => { setRecording(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        flexWrap: 'wrap', marginBottom: space(2),
      }}>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
          {['ALL', 'DRAFT', 'APPROVED', 'COMPLETED'].map((k) => (
            <Btn key={k} size="sm"
                 variant={filter === k ? 'primary' : 'secondary'}
                 onClick={() => setFilter(k)}>
              {k === 'ALL' ? 'All orders' : k.charAt(0) + k.slice(1).toLowerCase()}
            </Btn>
          ))}
        </div>
        <Btn onClick={() => setCreating(true)}>+ New production order</Btn>
      </div>

      {orders.length === 0 && (
        <Banner tone="info" title="No production orders yet">
          Create one to issue a batch number and print a requirement sheet.
          Nothing leaves the store until the order is approved.
        </Banner>
      )}

      <div style={{ display: 'grid', gap: space(2) }}>
        {orders.map((o) => {
          const perf = o.performance;
          return (
            <Card key={o.id}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                gap: space(1), flexWrap: 'wrap', alignItems: 'flex-start',
              }}>
                <div>
                  <div style={{ display: 'flex', gap: space(1), alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 650, fontSize: 14.5 }}>{o.order_number}</span>
                    <Chip tone={STATUS_TONE[(o.status || '').toUpperCase()] || 'neutral'}>
                      {o.status}
                    </Chip>
                    {o.batch_number && <Chip tone="neutral">Batch {o.batch_number}</Chip>}
                    {o.materials_owed > 0 && (
                      <Chip tone="warning">{o.materials_owed} material(s) owed</Chip>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: color.textSecondary, marginTop: 3 }}>
                    {o.product_name} · planned {num(o.quantity_planned, 0)}
                    {o.warehouse_name ? ` · ${o.warehouse_name}` : ''}
                    {o.approved_by_name ? ` · approved by ${o.approved_by_name}` : ''}
                  </div>
                  {o.approval_reason && (
                    <div style={{
                      marginTop: 6, fontSize: 12.5,
                      background: `${color.warning}14`,
                      borderLeft: `3px solid ${color.warning}`,
                      padding: `${space(0.75)} ${space(1)}`, borderRadius: radius.sm,
                    }}>
                      Approved short: {o.approval_reason}
                    </div>
                  )}
                  {o.damage_reason && (
                    <div style={{
                      marginTop: 6, fontSize: 12.5,
                      background: `${color.danger}10`,
                      borderLeft: `3px solid ${color.danger}`,
                      padding: `${space(0.75)} ${space(1)}`, borderRadius: radius.sm,
                    }}>
                      Damage: {o.damage_reason}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
                  <Btn size="sm" variant="secondary"
                       onClick={() => download(
                         `/api/bom/production-orders/${o.id}/requirements/download?format=pdf`,
                         `${o.order_number}-requirements.pdf`, notify)}>
                    Sheet (PDF)
                  </Btn>
                  <Btn size="sm" variant="secondary"
                       onClick={() => download(
                         `/api/bom/production-orders/${o.id}/requirements/download?format=csv`,
                         `${o.order_number}-requirements.csv`, notify)}>
                    CSV
                  </Btn>
                  {o.can_approve && (
                    <Btn size="sm" onClick={() => approve(o)} disabled={busy === o.id}>
                      Approve &amp; issue materials
                    </Btn>
                  )}
                  {o.can_record_output && (
                    <Btn size="sm" onClick={() => setRecording(o)}>
                      Record output
                    </Btn>
                  )}
                </div>
              </div>

              {perf && (
                <div style={{
                  marginTop: space(1.5), display: 'flex', gap: space(3),
                  flexWrap: 'wrap', background: color.bg,
                  padding: space(1.5), borderRadius: radius.md,
                }}>
                  {[
                    ['Good', num(perf.good, 0), null],
                    ['Damaged', num(perf.damaged, 0), perf.damaged > 0 ? color.danger : null],
                    ['Yield efficiency',
                      perf.yield_efficiency_pct === null ? '—' : `${perf.yield_efficiency_pct.toFixed(1)}%`,
                      perf.yield_efficiency_pct !== null && perf.yield_efficiency_pct < 90 ? color.warning : color.success],
                    ['Loss',
                      perf.loss_percentage_pct === null ? '—' : `${perf.loss_percentage_pct.toFixed(1)}%`,
                      perf.loss_percentage_pct > 0 ? color.danger : null],
                    ['Shortfall', num(perf.shortfall, 0), null],
                  ].map(([label, value, tone]) => (
                    <div key={label}>
                      <div style={{ fontSize: 12, color: color.textSecondary }}>{label}</div>
                      <div style={{
                        fontSize: 18, fontWeight: 650,
                        color: tone || color.text,
                      }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
